import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { WorkItemView } from '@/api/types';
import Pagination from '@/components/Pagination';
import { useTablePageSize } from '@/lib/useTablePageSize';
import { trim } from '@/lib/format';
import { actualEffortTone, effortToneClass } from '@/lib/effortTone';
import { formatEstimate, pertExpected } from '@/lib/estimate';
import { useEstimateMode } from '@/estimate/EstimateModeContext';
import { workItemRoute, workItemDisplayLabel } from '@/lib/workItems';

interface Props {
  workItems: WorkItemView[];
  /** Storage key for the page-size selector — same convention as other paginated tables. */
  tableId: string;
  emptyHint?: string;
}

/** Columns the table can sort by. `activity` (last merge) is the default. */
type SortKey =
  | 'workItem'
  | 'type'
  | 'developer'
  | 'prs'
  | 'actual'
  | 'aiEstimate'
  | 'noAiEstimate'
  | 'activity';

type SortDir = 'asc' | 'desc';

/** Numeric columns default to descending (biggest first); text columns to ascending. */
const NUMERIC_KEYS: ReadonlySet<SortKey> = new Set<SortKey>([
  'prs', 'actual', 'aiEstimate', 'noAiEstimate', 'activity',
]);

function sortValue(wi: WorkItemView, key: SortKey): number | string {
  switch (key) {
    case 'workItem':
      return workItemDisplayLabel(wi).heading.toLowerCase();
    case 'type':
      return (wi.issueType ?? '').toLowerCase();
    case 'developer':
      return wi.author.toLowerCase();
    case 'prs':
      return wi.pullRequestCount;
    case 'actual':
      // Untracked items sort below any tracked value regardless of direction intent —
      // they carry no comparable number, so -1 keeps them at the "no data" end.
      return wi.pullRequestsWithActual > 0 ? wi.actualHoursTotal : -1;
    case 'aiEstimate':
      return pertExpected(wi.estimatedHoursMin, wi.estimatedHoursLikely, wi.estimatedHoursMax) ?? -1;
    case 'noAiEstimate':
      return pertExpected(wi.unassistedHoursMin, wi.unassistedHoursLikely, wi.unassistedHoursMax) ?? -1;
    case 'activity':
      return new Date(wi.latestMergedAt).getTime();
  }
}

/**
 * Table-style work items list. Each row is one (primary Jira issue, author) bucket
 * — see WorkItemsService on the backend for the grouping rule. Clicking a row opens
 * the work-item detail page, similar to how the analyses table links to PR detail.
 *
 * Pagination and sorting are client-side: the backend returns the full list because
 * work-item rows per org are bounded and we already pay the round-trip; sorting and
 * cutting them into pages downstream keeps the UI consistent with the other tables.
 */
export default function WorkItemsView({ workItems, tableId, emptyHint }: Props) {
  const navigate = useNavigate();
  const { mode } = useEstimateMode();
  const [pageSize, setPageSize] = useTablePageSize(tableId, 20);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'activity',
    dir: 'desc',
  });

  const sorted = useMemo(() => {
    const rows = [...workItems];
    rows.sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      let cmp: number;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [workItems, sort]);

  const totalElements = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  const safePage = Math.min(page, totalPages - 1);

  // Reset to the first page when the underlying list shrinks (filter change, etc.) so we
  // don't get stuck on an out-of-range page showing nothing.
  useEffect(() => {
    if (page > 0 && page >= totalPages) setPage(0);
  }, [page, totalPages]);

  const visible = useMemo(
    () => sorted.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [sorted, safePage, pageSize],
  );

  const onPageSizeChange = (n: number) => {
    setPageSize(n);
    setPage(0);
  };

  const toggleSort = (key: SortKey) => {
    setPage(0);
    setSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: NUMERIC_KEYS.has(key) ? 'desc' : 'asc' },
    );
  };

  if (totalElements === 0) {
    return <p className="muted">{emptyHint ?? 'No work items in this slice.'}</p>;
  }

  const Th = ({ label, sortKey, className }: { label: string; sortKey: SortKey; className?: string }) => {
    const active = sort.key === sortKey;
    return (
      <th
        className={`sortable${active ? ' is-sorted' : ''}${className ? ` ${className}` : ''}`}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        onClick={() => toggleSort(sortKey)}
      >
        {label}
        <span className="sort-caret" aria-hidden>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </th>
    );
  };

  return (
    <>
      <div className="table-scroll">
        <table className="data-table data-table-hover">
          <thead>
            <tr>
              <Th label="Work item" sortKey="workItem" />
              <Th label="Type" sortKey="type" />
              <Th label="Developer" sortKey="developer" />
              <Th label="PRs" sortKey="prs" />
              <Th label="Actual" sortKey="actual" />
              <Th label="AI-assisted estimate" sortKey="aiEstimate" />
              <Th label="No-AI estimate" sortKey="noAiEstimate" />
              <Th label="Last activity" sortKey="activity" />
            </tr>
          </thead>
          <tbody>
            {visible.map(wi => {
              const label = workItemDisplayLabel(wi);
              const actual = wi.pullRequestsWithActual > 0 ? wi.actualHoursTotal : null;
              const toneCls = effortToneClass(
                actualEffortTone(actual, wi.estimatedHoursMax, wi.unassistedHoursMax),
              );
              return (
                <tr
                  key={`${wi.workItemKey}|${wi.author}`}
                  className="row-clickable"
                  onClick={() => navigate(workItemRoute(wi))}
                >
                  <td>
                    <strong>{label.heading}</strong>
                    {label.subtitle ? (
                      <div className="muted small">{label.subtitle}</div>
                    ) : null}
                  </td>
                  <td className="muted small">{wi.issueType ?? '—'}</td>
                  <td>@{wi.author}</td>
                  <td>{wi.pullRequestCount}</td>
                  <td className={toneCls}>
                    {wi.pullRequestsWithActual === 0
                      ? '—'
                      : `${trim(wi.actualHoursTotal)} h`}
                  </td>
                  <td>{formatEstimate(wi.estimatedHoursMin, wi.estimatedHoursLikely, wi.estimatedHoursMax, mode)}</td>
                  <td>{formatEstimate(wi.unassistedHoursMin, wi.unassistedHoursLikely, wi.unassistedHoursMax, mode)}</td>
                  <td className="muted small">{formatRelative(wi.latestMergedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={safePage}
        totalPages={totalPages}
        onPrev={() => setPage(p => Math.max(0, p - 1))}
        onNext={() => setPage(p => p + 1)}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const day = 24 * 3600 * 1000;
  if (diffMs < day) return 'today';
  if (diffMs < 2 * day) return 'yesterday';
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  if (diffMs < 30 * day) return `${Math.floor(diffMs / (7 * day))}w ago`;
  if (diffMs < 365 * day) return `${Math.floor(diffMs / (30 * day))}mo ago`;
  return `${Math.floor(diffMs / (365 * day))}y ago`;
}
